import api from "./api";
import {
  AttendanceRecord,
  AttendanceSummary,
  MarkAttendanceRequest,
  ZKTecoSyncRequest,
  AttendanceFilter,
  AttendanceReport,
  AttendanceTrend,
} from "../types/attendance";
import { PaginatedResponse } from "../types/common";

// Get attendance records with filtering and pagination
export const getAttendanceRecords = async (
  filters: AttendanceFilter = {}
): Promise<PaginatedResponse<AttendanceRecord>> => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, value.toString());
    }
  });

  const response = await api.get(`/attendance?${params}`);
  return response as any;
};

// Get attendance summary for a specific date
export const getAttendanceSummary = async (
  date?: string,
  userType?: string
): Promise<{ success: boolean; data: AttendanceSummary }> => {
  const params = new URLSearchParams();
  if (date) params.append("date", date);
  if (userType) params.append("userType", userType);

  const response = await api.get(`/attendance/summary?${params}`);
  return response as any;
};

// Mark attendance for a user
export const markAttendance = async (
  data: MarkAttendanceRequest
): Promise<{ success: boolean; data: AttendanceRecord }> => {
  const response = await api.post(`/attendance/mark`, data);
  return response as any;
};

// Clock out for a user
export const clockOut = async (
  userId: string,
  userType: string,
  clockOut?: string
): Promise<{ success: boolean; data: AttendanceRecord }> => {
  const response = await api.post(`/attendance/clock-out`, {
    userId,
    userType,
    clockOut,
  });
  return response as any;
};

// Update attendance record
export const updateAttendance = async (
  id: string,
  data: Partial<AttendanceRecord>
): Promise<{ success: boolean; data: AttendanceRecord }> => {
  const response = await api.put(`/attendance/${id}`, data);
  return response as any;
};

// Delete attendance record
export const deleteAttendance = async (
  id: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`/attendance/${id}`);
  return response as any;
};

// Sync attendance from ZKTeco device
export const syncFromZKTeco = async (
  data: ZKTecoSyncRequest
): Promise<{
  success: boolean;
  data: { syncedRecords: number; errors: string[] };
}> => {
  const response = await api.post(`/attendance/sync-zkteco`, data);
  return response as any;
};

// Get attendance dashboard data
export const getAttendanceDashboard = async (
  period?: string,
  userType?: string
): Promise<{
  success: boolean;
  data: {
    summary: AttendanceSummary;
    trends: AttendanceTrend[];
    recentRecords: AttendanceRecord[];
  };
}> => {
  const params = new URLSearchParams();
  if (period) params.append("period", period);
  if (userType) params.append("userType", userType);

  const response = await api.get(`/attendance/reports/dashboard?${params}`);
  return response as any;
};

// Get detailed attendance report
export const getDetailedAttendanceReport = async (
  filters: {
    userType?: string;
    dateFrom?: string;
    dateTo?: string;
    userId?: string;
    format?: "json" | "pdf" | "excel";
  } = {}
): Promise<{ success: boolean; data: AttendanceReport }> => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, value.toString());
    }
  });

  const response = await api.get(`/attendance/reports/detailed?${params}`);
  return response as any;
};

// Export attendance report
export const exportAttendanceReport = async (filters: {
  userType?: string;
  dateFrom?: string;
  dateTo?: string;
  format: "pdf" | "excel";
}): Promise<{ success: boolean; data: { downloadUrl: string } }> => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, value.toString());
    }
  });

  const response = await api.get(`/attendance/reports/export?${params}`);
  return response as any;
};

// Get attendance trends
export const getAttendanceTrends = async (
  period: "week" | "month" | "quarter" = "month",
  userType?: string
): Promise<{ success: boolean; data: AttendanceTrend[] }> => {
  const params = new URLSearchParams();
  params.append("period", period);
  if (userType) params.append("userType", userType);

  const response = await api.get(`/attendance/reports/trends?${params}`);
  return response as any;
};

// Bulk mark attendance
export const bulkMarkAttendance = async (
  records: MarkAttendanceRequest[]
): Promise<{
  success: boolean;
  data: {
    successful: number;
    failed: number;
    errors: string[];
  };
}> => {
  const response = await api.post(`/attendance/bulk-mark`, {
    records,
  });
  return response as any;
};

// Get my attendance records (for students)
export const getMyAttendance = async (
  filters: {
    page?: number;
    limit?: number;
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  } = {}
): Promise<{
  success: boolean;
  data: AttendanceRecord[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
  summary: {
    total: number;
    present: number;
    absent: number;
    late: number;
    excused: number;
    attendancePercentage: number;
  };
}> => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, value.toString());
    }
  });

  // The api instance automatically extracts response.data via interceptor
  // So 'response' here is actually the response.data object
  const response = await api.get(`/attendance/my-attendance?${params}`);
  return response as any;
};
